import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { resolveUsernameColor } from '../../utils/richText';

/**
 * Bridges "act on the message composer" intents from anywhere in the chat tree
 * (e.g. the RightSidebar row menu's "Mention") to the Lexical editor, WITHOUT
 * prop-drilling a ref through App -> ChatPanel.
 *
 * Why a context and not Redux: the composer's content lives inside Lexical's
 * own editor state, not React/Redux. Inserting a mention is an imperative
 * command to that editor ("put a pill at the caret now"), not a value to
 * render — so there's no slice to hold and we'd still need the editor handle.
 * The context just carries that handle to whoever needs to call it.
 *
 * The editor registers its imperative API via `registerComposer` (see
 * MessageEditor / EditorRefPlugin); consumers call the high-level helpers
 * (`insertMention`). The handle is kept in a ref so registering it does NOT
 * re-render consumers.
 */
const ComposerContext = createContext(null);

export function ComposerProvider({ children }) {
  const apiRef = useRef(null);

  // The composer calls this on mount to hand us its imperative API (or null on
  // unmount). Stable identity so MessageEditor can list it as an effect dep.
  const registerComposer = useCallback((api) => {
    apiRef.current = api;
  }, []);

  // Insert an @mention for `user` at the composer's caret. Builds the same
  // `data` the autocomplete attaches ({ id, usernameColor, avatar }) so the
  // inserted pill renders in the user's own color and serializes identically.
  const insertMention = useCallback((user) => {
    const api = apiRef.current;
    if (!api || !user || !user.username) return;
    api.insertMention(user.username, {
      id: user.id,
      usernameColor: resolveUsernameColor(user.usernameColor, user.role),
      avatar: user.avatar || null,
    });
  }, []);

  const value = useMemo(
    () => ({ registerComposer, insertMention }),
    [registerComposer, insertMention]
  );

  return <ComposerContext.Provider value={value}>{children}</ComposerContext.Provider>;
}

/**
 * Consume the composer bridge. Returns a no-op-safe object even outside a
 * provider, so a component can call `insertMention` without guarding.
 */
export function useComposer() {
  return useContext(ComposerContext) || EMPTY;
}

const EMPTY = {
  registerComposer: () => {},
  insertMention: () => {},
};
